
import React from 'react';
import Layout from '../components/Layout';
import { Book, Lightbulb, Bot, GraduationCap, Target, PenLine, CheckCircle, Dumbbell, LineChart, Shield, Eye, Sprout } from 'lucide-react';

const sections = [
  {
    icon: <Book size={38} color="#ff8a5c" />, // About Pascaline
    heading: 'About Pascaline',
    content: `Pascaline is an offline-first learning platform built to make quality education accessible to anyone, anywhere — regardless of internet access.\n\nAt its core, Pascaline combines structured curriculum learning with built-in AI that guides, assesses, and supports students in real time. From interactive lessons to automated marking, the system functions fully offline, turning any device into a complete and self-contained learning environment.`,
  },
  {
    icon: <Lightbulb size={38} color="#ff8a5c" />, // Why Pascaline Exists
    heading: 'Why Pascaline Exists',
    content: `Access to quality education is still limited by infrastructure, cost, and geography. Many students learn in environments without consistency, sufficient resources, or meaningful feedback.\n\nPascaline was built to remove those barriers.\n\nBy running entirely offline and requiring minimal setup, it allows students to learn independently, schools to operate more efficiently, and education to reach places where traditional systems struggle to serve.\n\nBut access alone is not enough.\n\nEducation must also build the ability to think, focus, and grow.`,
  },
  {
    icon: <Bot size={38} color="#ff8a5c" />, // What Makes It Different
    heading: 'What Makes It Different',
    content: `Pascaline is not just another learning app.\n\n* **Offline AI Marking**\n  Students receive immediate, meaningful feedback without needing internet access.\n\n* **Interactive Learning System**\n  Lessons are dynamic and responsive, requiring active engagement rather than passive consumption.\n\n* **Multi-Curriculum Support**\n  Designed to support different education systems and adapt over time.\n\n* **Self-Contained Learning Environment**\n  Each installation functions as a complete system — capable of teaching, assessing, and guiding without external dependency.`,
  },
  {
    icon: <GraduationCap size={38} color="#ff8a5c" />, // Our Philosophy
    heading: 'Our Philosophy',
    content: `Pascaline is built on a simple belief:\n\n**real learning requires focus, effort, and honesty.**\n\nIn a world designed to fragment attention, we take a different approach — not by adapting to distraction, but by building the ability to overcome it.`,
  },
  {
    icon: <Target size={34} color="#ff8a5c" />, // Focus Over Distraction
    heading: 'Focus Over Distraction',
    content: `Modern devices are filled with interruptions that weaken concentration.\n\nPascaline is designed for controlled learning environments.\nWe encourage the use of shared or dedicated devices where students can focus deeply without constant digital noise.\n\nBecause meaningful learning requires attention — not multitasking.`,
  },
  {
    icon: <PenLine size={34} color="#ff8a5c" />, // Depth Over Convenience
    heading: 'Depth Over Convenience',
    content: `Watching videos and selecting answers is not enough to build understanding.\n\nPascaline requires students to read, process, and express their thoughts through writing.\nEssays are not optional — they are central.\n\nBecause knowledge is not proven by recognition, but by expression.`,
  },
  {
    icon: <CheckCircle size={34} color="#ff8a5c" />, // Integrity Over Easy Progress
    heading: 'Integrity Over Easy Progress',
    content: `Progress should reflect true understanding, not repeated attempts.\n\nPascaline records first attempts as the measure of performance.\nStudents are encouraged to think before answering, not rely on trial and error.\n\nThere are no shortcuts to mastery.`,
  },
  {
    icon: <Dumbbell size={34} color="#ff8a5c" />, // Learning That Builds Strength
    heading: 'Learning That Builds Strength',
    content: `We do not reduce learning to constant entertainment.\n\nLearning should be engaging, but it must also challenge the student.\n\nPascaline is designed to develop individuals who are:\n\n* intellectually disciplined\n* capable of independent thinking\n* grounded in effort and consistency\n\nBecause the goal is not just to pass exams —\nbut to build strong minds.`,
  },
  {
    icon: <LineChart size={34} color="#ff8a5c" />, // A System That Develops, Not Just Measures
    heading: 'A System That Develops, Not Just Measures',
    content: `Some students already approach learning with discipline and intent.\n\nOthers are still developing that mindset.\n\nPascaline is designed for both.\n\nIt reveals true ability — while guiding students toward stronger habits, deeper focus, and better thinking over time.`,
  },
  {
    icon: <Shield size={34} color="#ff8a5c" />, // A System You Can Trust
    heading: 'A System You Can Trust',
    content: `Pascaline is built to be reliable, consistent, and resistant to manipulation.\n\nScores reflect actual performance.\nOutcomes are earned, not inflated.\n\nThis makes it suitable not just as a supplement, but as a complete learning system — including homeschooling environments.`,
  },
  {
    icon: <Eye size={38} color="#ff8a5c" />, // Our Vision
    heading: 'Our Vision',
    content: `We believe education should not depend on location, connectivity, or privilege.\n\nPascaline represents a future where learning is:\n\n* accessible\n* structured\n* intelligent\n* and honest\n\nA system that does not just deliver content —\nbut develops capability.`,
  },
  {
    icon: <Sprout size={38} color="#ff8a5c" />, // What We Stand For
    heading: 'What We Stand For',
    content: `We are not building for convenience.\nWe are building for growth.\n\nPascaline exists for students who are ready to take learning seriously —\nand for those who are willing to become so.\n\nBecause education is not just about what you learn…\n\n…it is about who you become.`,
  },
];

const renderMarkdown = (text: string) => {
  const parseInline = (value: string) => {
    let html = value
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>');

    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  };

  const lines = text.split('\n');
  const nodes: React.ReactNode[] = [];
  let listItems: React.ReactNode[] = [];

  const flushList = () => {
    if (listItems.length) {
      nodes.push(
        <ul key={`list-${nodes.length}`} style={{ margin: '0.5rem 0 1rem 1rem', padding: 0 }}>
          {listItems}
        </ul>
      );
      listItems = [];
    }
  };

  lines.forEach((line, idx) => {
    const trimmed = line.trim();

    if (trimmed === '---') {
      flushList();
      nodes.push(<hr key={`hr-${idx}`} style={{ margin: '2rem 0', border: 0, borderTop: '1px solid #eee' }} />);
      return;
    }

    if (trimmed.startsWith('* ')) {
      listItems.push(
        <li key={`li-${idx}`} style={{ marginBottom: 4, fontWeight: 500 }}>
          {parseInline(trimmed.slice(2))}
        </li>
      );
      return;
    }

    flushList();

    if (trimmed === '') {
      nodes.push(<br key={`br-${idx}`} />);
    } else {
      nodes.push(
        <p key={`p-${idx}`} style={{ margin: '0.4rem 0' }}>
          {parseInline(line)}
        </p>
      );
    }
  });

  flushList();

  return nodes;
};

const About = () => (
  <Layout>
    <main style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem 0' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
        {sections.map((section, idx) => (
          <section
            key={idx}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: '#fff',
              borderRadius: 18,
              boxShadow: '0 2px 16px rgba(30,41,59,0.07)',
              padding: '2.2rem 1.2rem',
              maxWidth: 600,
              margin: '0 auto',
              textAlign: 'center',
            }}
          >
            <div style={{ marginBottom: 18 }}>{section.icon}</div>
            <h2 style={{ fontSize: '1.45rem', fontWeight: 700, color: '#1a237e', marginBottom: 10 }}>{section.heading}</h2>
            <div style={{ fontSize: '1.13rem', color: '#374151', lineHeight: 1.7, textAlign: 'left', width: '100%', maxWidth: 520, margin: '0 auto' }}>
              {renderMarkdown(section.content)}
            </div>
          </section>
        ))}
      </div>
    </main>
  </Layout>
);

export default About;
